/**
 * 轮播/分页管理 Composable
 * 管理页面轮播、切换等功能
 */

import logger from '@/utils/logger';
import screenLifecycleStats from '@/utils/screenLifecycleStats';

export default function usePageRotation() {
  return {
    data() {
      return {
        currentPage: 0,
        pageEpoch: 0, // 历史字段：曾用于切页隔离图表；第一阶段优化后图表改按 roomId 管理，不再随切页递增
        isRotating: false,
        isSliding: false,
        slideAnimationId: null,
        slideAnimationOutTimer: null, // 出场动画兜底 setTimeout，与 slideAnimationInTimer 分开便于销毁时都清理
        slideAnimationInTimer: null, // 入场动画兜底 setTimeout
        checkSlidingTimer: null, // updateRotation 中“等待滑动结束”的 setInterval，组件销毁时需清理
        rotationSlidingRetryTimer: null, // rotationTimer 回调内“等滑动结束”的 1000ms setTimeout
        welcomeAnimationResetTimer: null, // 全屏欢迎语动画状态 600ms 重置
        slideCompleteBubbleTimers: [], // 轮播完成后延迟生成气泡的 setTimeout id 列表
        lastRotationEndTime: null, // 记录最后一次轮播结束的时间，用于延迟数据刷新
        pageTransition: {
          active: false,
          direction: 'next' // 'next' 或 'prev'
        },
        pendingPage: null // 待切换的页面，用于延迟内容更新
      };
    },

    computed: {
      totalPages() {
        // 确定每页需要的数量
        const isHalfScreenWelcome = this.welcomeMessage && 
          !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
        const pageSize = isHalfScreenWelcome ? 2 : 4;
        
        // 计算治疗中患者数量（用于计算页数）
        const ongoingPatients = this.activeAppointments.filter(item => 
          this.getTreatmentStatus(item) === '治疗中'
        );
        
        // 计算患者模块页数
        const patientPages = Math.ceil(ongoingPatients.length / pageSize);
        
        // 如果配置了全屏欢迎语，总页数 = 患者模块页数 + 1
        const hasFullscreenWelcome = this.welcomeConfig && 
          (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1') &&
          this.welcomeConfig.welcomeText;
        
        let total = patientPages;
        
        // 如果有全屏欢迎语，添加欢迎语页
        if (hasFullscreenWelcome) {
          total += 1;
        }
        
        // 如果有视频，添加视频页
        const hasVideos = this.videoList && this.videoList.length > 0;
        if (hasVideos) {
          total += 1;
        }
        
        return total;
      },

      isVideoPage() {
        // 判断当前页是否为视频页
        const hasVideos = this.videoList && this.videoList.length > 0;
        if (!hasVideos) return false;
        
        // 确定每页需要的数量
        const isHalfScreenWelcome = this.welcomeMessage && 
          !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
        const pageSize = isHalfScreenWelcome ? 2 : 4;
        
        // 计算治疗中患者数量
        const ongoingPatients = this.activeAppointments.filter(item => 
          this.getTreatmentStatus(item) === '治疗中'
        );
        
        // 计算患者模块页数
        const patientPages = Math.ceil(ongoingPatients.length / pageSize);
        
        // 判断是否为全屏模式
        const isFullscreen = this.welcomeConfig && 
          (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1') &&
          this.welcomeConfig.welcomeText;
        
        if (isFullscreen) {
          // 全屏模式：患者页(0~n-1) → 欢迎语页(n) → 视频页(n+1)
          return this.currentPage === patientPages + 1;
        } else {
          // 半屏模式：患者页(0~n-1) → 视频页(n)
          return this.currentPage === patientPages;
        }
      },

      isFullscreenWelcomePage() {
        // 判断当前页是否为全屏欢迎语页
        const hasFullscreenWelcome = this.welcomeConfig && 
          (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1') &&
          this.welcomeConfig.welcomeText;
        if (!hasFullscreenWelcome) return false;
        
        // 确定每页需要的数量
        const isHalfScreenWelcome = this.welcomeMessage && 
          !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
        const pageSize = isHalfScreenWelcome ? 2 : 4;
        
        // 计算治疗中患者数量
        const ongoingPatients = this.activeAppointments.filter(item => 
          this.getTreatmentStatus(item) === '治疗中'
        );
        
        const patientPages = Math.ceil(ongoingPatients.length / pageSize);
        
        // 全屏模式：患者页(0~n-1) → 欢迎语页(n) → 视频页(n+1)
        // 如果没有患者（patientPages = 0），且 currentPage = 0，显示欢迎语页（这是合理的）
        // 如果有患者（patientPages > 0），只有当 currentPage === patientPages 时才是欢迎语页
        if (patientPages === 0) {
          // 没有患者时，欢迎语页是第0页
          return this.currentPage === 0;
        } else {
          // 有患者时，欢迎语页在患者页之后
          return this.currentPage === patientPages;
        }
      },

      needsRotation() {
        const ongoingPatients = this.activeAppointments.filter(item => this.getTreatmentStatus(item) === '治疗中');
        
        // 如果没有治疗中的患者，禁用轮播
        if (ongoingPatients.length === 0) {
          return false;
        }
        
        // 优先检查 totalPages > 1，因为 totalPages 已经包含了全屏欢迎语页的情况
        // 当只有一页患者页面但存在全屏欢迎语时，totalPages = 2，需要轮播
        // 当只有一页患者页面且不存在全屏欢迎语时，totalPages = 1，不需要轮播
        return this.totalPages > 1;
      },

      currentPageDisplay() {
        // 用于显示当前页码，从1开始计数
        return this.totalPages > 0 ? this.currentPage + 1 : 0;
      }
    },

    methods: {
      clearRotationTimer() {
        if (this.rotationTimer) {
          // rotationTimer 可能来自 setInterval 或 setTimeout，这里统一清理
          clearTimeout(this.rotationTimer);
          clearInterval(this.rotationTimer);
          this.rotationTimer = null;
        }
      },

      getCurrentRotationDurationMs() {
        // 允许主组件覆盖（按“全屏欢迎语/患者页/视频页”返回不同秒数）
        if (typeof this.getRotationDurationMs === 'function') {
          try {
            const v = this.getRotationDurationMs();
            // v <= 0：表示当前页面不使用定时轮播（例如视频“播完再切”模式）
            if (typeof v === 'number' && isFinite(v)) {
              if (v <= 0) return 0;
              return v;
            }
          } catch (e) {
            // ignore
          }
        }
        return 15000;
      },

      /**
       * 启动轮播
       */
      startRotation(keepPage = false) {
        if (!keepPage) {
          this.currentPage = 0;
        } else {
          // 保持当前页码，但需验证有效性
          if (this.currentPage >= this.totalPages) {
            this.currentPage = 0;
          }
        }

        this.isRotating = false;
        this.isSliding = false;

        const stopRotation = () => {
          this.clearRotationTimer();
        };

        if (this.appointmentData.length === 0) {
          stopRotation();
          if (this.addSystemLog) {
            this.addSystemLog('没有预约数据，轮播功能未启动');
          }
          return;
        }

        const totalPatients = this.activeAppointments.length;
        const ongoingPatients = this.activeAppointments.filter(item => this.getTreatmentStatus(item) === '治疗中');
        const ongoingCount = ongoingPatients.length;
        if (totalPatients === 0) {
          stopRotation();
          if (this.addSystemLog) {
            this.addSystemLog('没有预约数据，轮播功能未启动');
          }
          return;
        }

        // 直接使用 needsRotation 计算属性来判断是否需要轮播
        if (!this.needsRotation) {
          stopRotation();
          if (this.addSystemLog) {
            this.addSystemLog('不满足轮播条件，页面轮播功能未启动');
          }
          return;
        }

        // 轮播调度：每次根据“当前页面类型”动态计算下一次切换时间
        // 使用 setTimeout 而不是固定 setInterval，便于欢迎语/患者页/视频页各自配置秒数
        stopRotation();

        const schedule = () => {
          const delay = this.getCurrentRotationDurationMs();
          // delay=0 表示暂停定时轮播（等待外部事件触发切页，如视频 ended）
          if (!delay) {
            this.clearRotationTimer();
            return;
          }
          this.rotationTimer = setTimeout(() => {
            // 如果正在滑动动画中，稍后再试（避免打断动画）
            if (this.isSliding) {
              if (this.rotationSlidingRetryTimer) {
                clearTimeout(this.rotationSlidingRetryTimer);
                this.rotationSlidingRetryTimer = null;
              }
              this.rotationSlidingRetryTimer = setTimeout(() => {
                this.rotationSlidingRetryTimer = null;
                schedule();
              }, 500);
              return;
            }

            // 轮播前钩子（例如：视频页到点切换前，先暂停并记录断点）
            if (typeof this.onBeforeRotate === 'function') {
              try {
                this.onBeforeRotate();
              } catch (e) {
                // ignore
              }
            }

            // 到点强制切页（视频页也要强制切，不等 ended）
            this.rotateModules('next', { force: true, reason: 'timer' });

            // 图表 init 由 startSlideAnimation.finish 单入口负责，此处不再重复调用
            this.$nextTick(() => {
              schedule();
            });
          }, delay);
        };

        schedule();

        if (this.addSystemLog) {
          this.addSystemLog(`治疗中患者数为${ongoingCount}，满足轮播条件，页面轮播功能已启动`);
        }
      },

      /**
       * 更新轮播
       */
      updateRotation() {
        // 如果正在滑动动画中，延迟更新，防止打断动画导致跳转
        if (this.isSliding) {
          if (this.checkSlidingTimer) {
            clearInterval(this.checkSlidingTimer);
            this.checkSlidingTimer = null;
          }
          this.checkSlidingTimer = setInterval(() => {
            if (!this.isSliding) {
              clearInterval(this.checkSlidingTimer);
              this.checkSlidingTimer = null;
              this.updateRotation();
            }
          }, 100);
          return;
        }

        this.isRotating = false;
        this.startRotation(true);

        // 图表 init 由轮播动画 finish 统一触发，此处仅恢复气泡定时器
        this.$nextTick(() => {
          // 重新初始化气泡定时器，确保轮播后气泡继续产生
          if (this.bubbleTimer) {
            clearInterval(this.bubbleTimer);
          }
          
          // 低性能模式下允许关闭气泡定时器（bubbleUpdateMs=0）
          const bubbleUpdateMs = Number(this.performanceConfig && this.performanceConfig.bubbleUpdateMs);
          if (bubbleUpdateMs > 0) {
            this.bubbleTimer = setInterval(() => {
              if (this.updateBubbles) {
                this.updateBubbles();
              }
            }, bubbleUpdateMs);
            if (this.updateBubbles) {
              this.updateBubbles();
            }
          } else {
            this.bubbleTimer = null;
          }
        });
      },
          startSlideAnimation(direction, nextPage, onComplete) {
            // 关键调试：记录轮播切换前的数据快照
            const dataSnapshot = this.appointmentData.map(app => ({
              roomId: app.roomId,
              roomName: app.roomName,
              delta: app.delta,
              theta: app.theta,
              alpha: app.alpha,
              beta: app.beta,
              gamma: app.gamma,
              hasRealtimeArray: !!(app.realtimeDataArray && app.realtimeDataArray.length > 0),
              hasLastValid: !!app.lastValidBrainwaveData,
              lastReceiveTime: app.lastReceiveTime
            }));
            logger.debug(`[轮播切换] startSlideAnimation: 从第${this.currentPage + 1}页切换到第${nextPage + 1}页`, {
              direction,
              currentPage: this.currentPage,
              nextPage,
              appointmentCount: this.appointmentData.length,
              dataSnapshot
            });
            
            const modulesWrapper = this.$refs.sleepView && this.$refs.sleepView.$refs.modulesWrapper;
            if (!modulesWrapper) {
              return;
            }
      
            // 第一阶段优化：切页不再递增 pageEpoch / 全量 dispose 图表。
            // 图表按 roomId 复用，仅清理脱离 DOM 的孤儿实例（initCharts 内 detachOrphanChartInstances）。
            const view = this.$refs.sleepView;
            if (view && view.detachOrphanChartInstances) {
              view.detachOrphanChartInstances();
            } else if (view && view.cleanupOffPageCharts) {
              view.cleanupOffPageCharts();
            }
            // 预加载配置会在 initCharts 中使用后被自动清理
      
            // 统一时长 200ms + 200ms，总时长约 400ms
            const phaseDuration = 200;
            const outClass = direction === 'next' ? 'slide-out-left' : 'slide-out-right';
            const inClass = direction === 'next' ? 'slide-in-right' : 'slide-in-left';
      
            // 标记进入滑动状态，并保存待切换的页面
            this.isSliding = true;
            this.isRotating = true;
            this.pendingPage = nextPage; // 保存待切换的页面，延迟到动画开始后再更新
      
            // 清除可能遗留的动画类
            modulesWrapper.classList.remove('slide-in-right', 'slide-in-left', 'slide-out-left', 'slide-out-right');
      
            // 触发出场动画
            // 修复：强制重排，确保动画每次都能启动
            void modulesWrapper.offsetWidth;
            void modulesWrapper.offsetHeight;
            // 修复：使用 requestAnimationFrame 确保在下一帧添加动画类
            requestAnimationFrame(() => {
              modulesWrapper.classList.add(outClass);
            });
      
            let outDone = false;
            let inDone = false;
      
            const finish = () => {
              if (!inDone) return;
              modulesWrapper.classList.remove('slide-in-right', 'slide-in-left', 'slide-out-left', 'slide-out-right');
              // 确保清除opacity
              modulesWrapper.style.opacity = '';
              
              this.isSliding = false;
              this.isRotating = false;
              // 关键修复：记录轮播结束时间，用于延迟数据刷新
              this.lastRotationEndTime = Date.now();
              this.pendingPage = null; // 清除待切换页面
              this.slideAnimationId = null;
              
              // 关键调试：记录轮播切换后的数据快照
              const afterDataSnapshot = this.appointmentData.map(app => ({
                roomId: app.roomId,
                roomName: app.roomName,
                delta: app.delta,
                theta: app.theta,
                alpha: app.alpha,
                beta: app.beta,
                gamma: app.gamma,
                hasRealtimeArray: !!(app.realtimeDataArray && app.realtimeDataArray.length > 0),
                hasLastValid: !!app.lastValidBrainwaveData,
                lastReceiveTime: app.lastReceiveTime
              }));
              logger.debug(`[轮播切换] startSlideAnimation完成: 当前页${this.currentPage + 1}`, {
                appointmentCount: this.appointmentData.length,
                afterDataSnapshot
              });
              
              // 动画完成后：先 initCharts，完成后再 updateAllCharts（避免时序竞态）
              this.$nextTick(() => {
                if (typeof onComplete === 'function') {
                  onComplete();
                }
                const viewAfter = this.$refs.sleepView;
                if (viewAfter && viewAfter.initCharts) {
                  viewAfter.initCharts(() => {
                    if (this.updateAllCharts) {
                      this.updateAllCharts();
                    }
                  });
                } else if (this.updateAllCharts) {
                  this.updateAllCharts();
                }
                
                // 确保预加载下一页（为下次切换做准备）
                // 计算下一页的索引
                const isHalfScreenWelcome = this.welcomeMessage && 
                  !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
                const pageSize = isHalfScreenWelcome ? 2 : 4;
                const ongoingPatients = this.activeAppointments.filter(item => 
                  this.getTreatmentStatus(item) === '治疗中'
                );
                const patientPages = Math.ceil(ongoingPatients.length / pageSize);
                const totalPages = this.totalPages;
                const upcomingPage = (nextPage + 1) % totalPages;
                // 预加载下一页（如果存在）
                if (upcomingPage !== nextPage && upcomingPage !== patientPages) {
                  this.preloadNextPage(upcomingPage);
                }
              });
            };
      
            const startInPhase = () => {
              const fromPage = this.currentPage;
              this.currentPage = this.pendingPage;
              screenLifecycleStats.recordPageRotation(fromPage, this.currentPage);
              
              // 修复：使用 $nextTick 确保 Vue 已更新 DOM，然后触发动画
              this.$nextTick(() => {
                const view = this.$refs.sleepView;
                if (view && typeof view.clearVisibleSlotCharts === 'function') {
                  view.clearVisibleSlotCharts();
                }

                // 防止闪烁：先隐藏元素，再移除outClass
                modulesWrapper.style.opacity = '0';
                modulesWrapper.classList.remove('slide-out-left', 'slide-out-right');
                
                // 修复：强制重排，确保动画每次都能正确触发
                void modulesWrapper.offsetWidth;
                void modulesWrapper.offsetHeight;
                
                // 修复：使用双重 requestAnimationFrame 确保在下一帧添加动画类
                // 这样可以确保内容已经更新到DOM中，然后再触发动画
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    // 确保内容已经渲染完成后再添加动画类
                    modulesWrapper.classList.add(inClass);
                    // 恢复opacity，让动画控制
                    modulesWrapper.style.opacity = '';
                  });
                });
              });
      
              // 监听入场动画结束
              const onInEnd = (e) => {
                if (e && e.target !== modulesWrapper) return;
                inDone = true;
                modulesWrapper.removeEventListener('animationend', onInEnd);
                finish();
              };
              modulesWrapper.addEventListener('animationend', onInEnd);
              // 兜底定时器，防止某些情况下 animationend 未触发
              if (this.slideAnimationInTimer) clearTimeout(this.slideAnimationInTimer);
              this.slideAnimationInTimer = setTimeout(() => {
                if (!inDone) {
                  inDone = true;
                  modulesWrapper.removeEventListener('animationend', onInEnd);
                  finish();
                }
                this.slideAnimationInTimer = null;
              }, phaseDuration + 100);
            };
      
            // 监听出场动画结束
            const onOutEnd = (e) => {
              if (e && e.target !== modulesWrapper) return;
              if (outDone) return;
              outDone = true;
              modulesWrapper.removeEventListener('animationend', onOutEnd);
              startInPhase();
            };
            modulesWrapper.addEventListener('animationend', onOutEnd);
            // 兜底定时器，防止某些情况下 animationend 未触发
            if (this.slideAnimationOutTimer) clearTimeout(this.slideAnimationOutTimer);
            this.slideAnimationOutTimer = setTimeout(() => {
              if (!outDone) {
                outDone = true;
                modulesWrapper.removeEventListener('animationend', onOutEnd);
                startInPhase();
              }
              this.slideAnimationOutTimer = null;
            }, phaseDuration + 100);
          },
      
          // 动画中断处理，确保状态重置
          cancelSlideAnimation(reason = '') {
            if (this.slideAnimationId) {
              try {
                cancelAnimationFrame(this.slideAnimationId);
              } catch (e) {
                clearTimeout(this.slideAnimationId);
              }
              this.slideAnimationId = null;
            }
            if (this.slideAnimationOutTimer) {
              clearTimeout(this.slideAnimationOutTimer);
              this.slideAnimationOutTimer = null;
            }
            if (this.slideAnimationInTimer) {
              clearTimeout(this.slideAnimationInTimer);
              this.slideAnimationInTimer = null;
            }
            if (this.welcomeAnimationResetTimer) {
              clearTimeout(this.welcomeAnimationResetTimer);
              this.welcomeAnimationResetTimer = null;
            }
            if (this.slideCompleteBubbleTimers && this.slideCompleteBubbleTimers.length > 0) {
              this.slideCompleteBubbleTimers.forEach(tid => clearTimeout(tid));
              this.slideCompleteBubbleTimers = [];
            }
            const modulesWrapper = this.$refs.sleepView && this.$refs.sleepView.$refs.modulesWrapper;
            if (modulesWrapper) {
              modulesWrapper.classList.remove('slide-in-right', 'slide-in-left', 'slide-out-left', 'slide-out-right');
            }
            this.isSliding = false;
            this.isRotating = false;
            // 关键修复：记录轮播结束时间，用于延迟数据刷新
            this.lastRotationEndTime = Date.now();
            this.pendingPage = null; // 清除待切换页面
            if (reason) {
              this.addSystemLog(`轮播动画中断${reason ? '：' + reason : ''}`);
            }
          },
      
          rotateModules(direction = 'next', options = {}) {
            if (!this.needsRotation) return;
      
            // 使用activeAppointments的长度来计算轮播逻辑
            const appointmentCount = this.activeAppointments.length;
            if (appointmentCount === 0) return;
      
            // 使用计算属性 totalPages（包含全屏欢迎语页）
            if (this.totalPages <= 1) return;
      
            // 如果已经在滑动中，不要重复触发
            if (this.isSliding) return;
            
            // 关键调试：记录rotateModules调用时的数据状态
            const rotateDataSnapshot = this.appointmentData.map(app => ({
              roomId: app.roomId,
              roomName: app.roomName,
              delta: app.delta,
              theta: app.theta,
              alpha: app.alpha,
              beta: app.beta,
              gamma: app.gamma,
              hasRealtimeArray: !!(app.realtimeDataArray && app.realtimeDataArray.length > 0),
              hasLastValid: !!app.lastValidBrainwaveData
            }));
            logger.debug(`[轮播切换] rotateModules调用: direction=${direction}, currentPage=${this.currentPage}`, {
              appointmentCount: this.appointmentData.length,
              rotateDataSnapshot
            });
            
            const force = !!(options && options.force);
            // 如果视频正在播放，默认不切换；但允许“到点强制切换”
            if (!force && this.isVideoPlaying && this.isVideoPage) return;
      
            // 根据方向计算下一页
            let nextPage;
            if (direction === 'next') {
              nextPage = (this.currentPage + 1) % this.totalPages;
            } else {
              nextPage = (this.currentPage - 1 + this.totalPages) % this.totalPages;
            }
      
            // 确定每页需要的数量
            const isHalfScreenWelcome = this.welcomeMessage && 
              !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
            const pageSize = isHalfScreenWelcome ? 2 : 4;
            
            // 计算治疗中患者数量
            const ongoingPatients = this.activeAppointments.filter(item => 
              this.getTreatmentStatus(item) === '治疗中'
            );
            
            // 计算患者模块页数
            const patientPages = Math.ceil(ongoingPatients.length / pageSize);
            
            // 判断是否为全屏模式
            const isFullscreen = this.welcomeConfig && 
              (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1') &&
              this.welcomeConfig.welcomeText;
            
            // 判断是否为视频页
            const isNextPageVideo = isFullscreen 
              ? nextPage === patientPages + 1 
              : nextPage === patientPages;
            const isCurrentPageVideo = isFullscreen 
              ? this.currentPage === patientPages + 1 
              : this.currentPage === patientPages;
            
            // 判断是否为全屏欢迎语页
            const isNextPageFullscreen = isFullscreen && nextPage === patientPages;
            const isCurrentPageFullscreen = isFullscreen && this.currentPage === patientPages;
      
            // 如果切换到全屏欢迎语页、视频页或从这些页面切换，直接更新页面，不执行滑动动画
            if (isNextPageFullscreen || isCurrentPageFullscreen || isNextPageVideo || isCurrentPageVideo) {
              const fromPage = this.currentPage + 1;
              const toPage = nextPage + 1;
              let pageType = '';
              if (isNextPageFullscreen || isCurrentPageFullscreen) {
                pageType = '（全屏欢迎语页）';
              } else if (isNextPageVideo || isCurrentPageVideo) {
                pageType = '（视频页）';
              }
              this.addSystemLog(`页面切换：从第${fromPage}页切换到第${toPage}页${pageType}`);
              
              const prevPageIndex = this.currentPage;
              this.currentPage = nextPage;
              screenLifecycleStats.recordPageRotation(prevPageIndex, this.currentPage);
              
              // 如果切换到全屏欢迎语页，设置动画状态
              if (isNextPageFullscreen) {
                this.welcomeAnimationState = 'entering';
                if (this.welcomeAnimationResetTimer) {
                  clearTimeout(this.welcomeAnimationResetTimer);
                  this.welcomeAnimationResetTimer = null;
                }
                this.welcomeAnimationResetTimer = setTimeout(() => {
                  this.welcomeAnimationResetTimer = null;
                  this.welcomeAnimationState = '';
                }, 600);
              }
              
              return;
            }
      
            // 统一方向：始终使用向左的动画（出场向左，入场从右）
            const actualDirection = 'next';
      
            const fromPage = this.currentPage + 1;
            const toPage = nextPage + 1;
            this.addSystemLog(`页面切换：从第${fromPage}页切换到第${toPage}页`);
      
            // 设置滑动方向（使用实际方向）
            this.slideDirection = actualDirection;
      
            // 使用 rAF 执行 300ms 动画并在完成后做清理（使用实际方向）
            this.startSlideAnimation(actualDirection, nextPage, () => {
              // 动画完成后的回调：气泡处理（图表已在 startSlideAnimation.finish 中 init）
              // 优化：智能处理气泡，保留有效气泡，避免重复生成
              const currentPageAppointments = this.displayedAppointments.filter(a => a);
              
              // 优化：不再清空离屏患者的气泡，保留它们在内存中以便快速复用
              // 只清理过期气泡（在updateBubbles中会自动处理）
              
              // 渐进式为当前页患者生成气泡（只在需要时生成）
              currentPageAppointments.forEach(item => {
                if (item) {
                  // 初始化气泡数组（如果不存在）
                  if (!item.bubbles) {
                    this.$set(item, 'bubbles', []);
                  }
                  if (!item.lastBubbleTime) {
                    this.$set(item, 'lastBubbleTime', 0);
                  }
                  
                  // 检查是否有有效气泡
                  const validBubbles = this.filterValidBubbles(item.bubbles);
                  
                  // 如果没有有效气泡，才重置时间并生成新气泡
                  if (validBubbles.length === 0) {
                    const randomDelay = Math.floor(Math.random() * 500); // 0-0.5秒随机延迟
                    const tid = setTimeout(() => {
                      item.lastBubbleTime = 0;
                      this.updateSinglePatientBubble(item);
                    }, randomDelay);
                    this.slideCompleteBubbleTimers.push(tid);
                  }
                  // 如果有有效气泡，保留它们，不重置lastBubbleTime，避免重复生成
                }
              });
      
              // 在轮播完成后预加载"下一页"的内容与图表（使用原始方向计算）
              let upcomingPage;
              if (direction === 'next') {
                upcomingPage = (nextPage + 1) % this.totalPages;
              } else {
                upcomingPage = (nextPage - 1 + this.totalPages) % this.totalPages;
              }
              this.preloadNextPage(upcomingPage);
            });
          },
          // 图片预加载方法已提取到 useBubbles composable
          
      // 因为它们需要访问很多主组件的状态和方法（如 startSlideAnimation, initCharts 等）
    }
  };
}

